import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useGeo } from "../lib/locale";
import ListingCard from "../components/ListingCard";
import { CATEGORIES, CONDITIONS, TID } from "../constants/catalog";
import { Search, SlidersHorizontal, MapPin, Locate } from "lucide-react";

const LOC_KEY = "pbp:searchLoc";

export default function Browse() {
  const geo = useGeo();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "All");
  const [condition, setCondition] = useState(params.get("condition") || "All");
  const [sellerType, setSellerType] = useState(params.get("seller_type") || "All");
  const [sort, setSort] = useState(params.get("sort") || "newest");
  const [country, setCountry] = useState(params.get("country") ?? (geo?.country || ""));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Location state: {label, lat, lng, source}. Persisted to sessionStorage.
  const [loc, setLoc] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(LOC_KEY) || "null"); } catch { return null; }
  });
  const [locBusy, setLocBusy] = useState(false);
  const [locInput, setLocInput] = useState("");

  // On first load, if no session loc, seed from IP geo
  useEffect(() => {
    if (loc || !geo?.country) return;
    const seed = { label: [geo.city, geo.country].filter(Boolean).join(", "), lat: geo.lat, lng: geo.lng, source: "ip" };
    setLoc(seed);
    sessionStorage.setItem(LOC_KEY, JSON.stringify(seed));
    // eslint-disable-next-line
  }, [geo?.country]);

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported by this browser");
    setLocBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const next = { label: "Your current location", lat: pos.coords.latitude, lng: pos.coords.longitude, source: "gps" };
        setLoc(next);
        sessionStorage.setItem(LOC_KEY, JSON.stringify(next));
        toast.success("Using your current location");
        setLocBusy(false);
      },
      (err) => {
        setLocBusy(false);
        toast.error(err.code === 1 ? "Location permission denied" : "Couldn't detect your location");
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
    );
  };

  const applyLocInput = () => {
    const label = locInput.trim();
    if (!label) return;
    // simple keyword mode — search all listings that match the text in city/country
    const next = { label, lat: null, lng: null, source: "manual" };
    setLoc(next);
    sessionStorage.setItem(LOC_KEY, JSON.stringify(next));
    setCountry(""); // city text becomes part of q via backend cross-field search
    setQ((old) => (old ? old : label));
    setLocInput("");
  };

  const clearLoc = () => {
    setLoc(null);
    sessionStorage.removeItem(LOC_KEY);
    toast("Now searching all locations");
  };

  const queryParams = useMemo(() => {
    const p = { sort, limit: 60 };
    if (q) p.q = q;
    if (category && category !== "All") p.category = category;
    if (condition && condition !== "All") p.condition = condition;
    if (sellerType && sellerType !== "All") p.seller_type = sellerType;
    if (country) p.country = country;
    if (loc?.lat != null) p.lat = loc.lat;
    if (loc?.lng != null) p.lng = loc.lng;
    return p;
  }, [q, category, condition, sellerType, sort, country, loc?.lat, loc?.lng]);

  useEffect(() => {
    setLoading(true);
    api
      .get("/listings", { params: queryParams })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    const sp = new URLSearchParams();
    if (q) sp.set("q", q);
    if (category !== "All") sp.set("category", category);
    if (condition !== "All") sp.set("condition", condition);
    if (sellerType !== "All") sp.set("seller_type", sellerType);
    if (sort !== "newest") sp.set("sort", sort);
    if (country) sp.set("country", country);
    setParams(sp, { replace: true });
  }, [queryParams]);

  return (
    <div className="pbp-section py-8 md:py-12">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#86868b] font-semibold">Marketplace</div>
          <h1 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight">Browse Apple parts</h1>
        </div>
        <div className="relative md:w-96">
          <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-[#86868b]" strokeWidth={1.5} />
          <input
            data-testid={TID.browse.search}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search model, part, city, description"
            className="pbp-input pl-11"
          />
        </div>
      </div>

      {/* Location strip */}
      <div className="flex flex-wrap items-center gap-3 mb-6 rounded-2xl bg-white border border-[#d2d2d7] px-4 py-3" data-testid="browse-location-strip">
        <MapPin className="w-4 h-4 text-[#0071e3]" strokeWidth={1.8} />
        <div className="text-sm">
          {loc ? (
            <>Showing listings near <b data-testid="browse-location-label">{loc.label}</b> <span className="text-[#86868b]">({loc.source === "gps" ? "current location" : loc.source === "ip" ? "detected via IP" : "manual"})</span></>
          ) : (
            <span className="text-[#86868b]">Searching all locations</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          <button data-testid="browse-use-location" onClick={useMyLocation} disabled={locBusy} className="pbp-btn-ghost text-[12px] !px-3 !py-1.5">
            <Locate className="w-3.5 h-3.5" strokeWidth={1.8} /> {locBusy ? "Detecting…" : "Use my location"}
          </button>
          <div className="flex items-center gap-1">
            <input
              value={locInput}
              onChange={(e) => setLocInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyLocInput()}
              placeholder="City, region or country"
              data-testid="browse-location-input"
              className="pbp-input !py-1.5 text-[13px] w-56"
            />
            <button data-testid="browse-location-apply" onClick={applyLocInput} className="pbp-btn-secondary text-[12px] !px-3 !py-1.5">Set</button>
          </div>
          {loc && (
            <button data-testid="browse-location-clear" onClick={clearLoc} className="pbp-btn-ghost text-[12px] !px-3 !py-1.5">Change</button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-8 items-center">
        <SlidersHorizontal className="w-4 h-4 text-[#86868b]" />
        <FilterSelect testId={TID.browse.filterCategory} value={category} onChange={setCategory} options={["All", ...CATEGORIES]} label="Category" />
        <FilterSelect testId={TID.browse.filterCondition} value={condition} onChange={setCondition} options={["All", ...CONDITIONS]} label="Condition" />
        <FilterSelect testId="filter-seller-type" value={sellerType} onChange={setSellerType} options={["All", "End User", "Repair Shop", "Just Liquidator"]} label="Seller" />
        <FilterSelect testId={TID.browse.filterSort} value={sort} onChange={setSort}
          options={[
            { v: "newest", l: "Newest" },
            { v: "nearest", l: "Nearest" },
            { v: "price_asc", l: "Price: Low to high" },
          ]} label="Sort" />
        <FilterSelect testId={TID.browse.filterCountry} value={country || ""} onChange={setCountry}
          options={[{ v: "", l: "Global" }, { v: "India", l: "India" }, { v: "United States", l: "United States" }, { v: "United Kingdom", l: "United Kingdom" }, { v: "Germany", l: "Germany" }, { v: "Singapore", l: "Singapore" }, { v: "Australia", l: "Australia" }, { v: "United Arab Emirates", l: "UAE" }]}
          label="Region" />
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="pbp-card animate-pulse">
              <div className="aspect-[4/3] bg-[#eaeaef]" />
              <div className="p-5 space-y-2">
                <div className="h-3 w-20 bg-[#eaeaef] rounded" />
                <div className="h-4 w-3/4 bg-[#eaeaef] rounded" />
                <div className="h-3 w-1/2 bg-[#eaeaef] rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="pbp-card p-12 text-center text-[#86868b]">
          No matches. Try clearing filters or searching a different model number.
        </div>
      ) : (
        <div data-testid={TID.browse.grid} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 pbp-stagger">
          {items.map((l) => (
            <ListingCard key={l.listing_id} listing={l} geoCountryCode={geo?.country_code} />
          ))}
        </div>
      )}
    </div>
  );
}

const FilterSelect = ({ testId, value, onChange, options, label }) => {
  const opts = options.map((o) => (typeof o === "string" ? { v: o, l: o } : o));
  return (
    <div className="relative">
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        className="appearance-none bg-white border border-[#d2d2d7] rounded-full pl-4 pr-9 py-2 text-sm font-medium hover:border-[#0071e3] focus:outline-none focus:ring-2 focus:ring-[#0071e3]"
      >
        {opts.map((o) => (
          <option key={o.v} value={o.v}>
            {label}: {o.l}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#86868b] text-xs">▾</span>
    </div>
  );
};
